<template>
  <section class="w-full">
    <form
      @submit.prevent="
       Object.keys(props.skillToUpdate).length > 0 
       ? updateSkill() 
       : addNewSkill()
      "
      class="grid grid-cols-12 gap-4 px-4 mt-3 text-base font-normal"
    >
      <span class="col-span-12">
        <label class="required-sign">{{ translate("skill name", "ucfirst") }}</label>
        <CustomInputText name="skill_name" class="w-full px-3 py-2 mt-2.5 rounded-full border border-iron" />
      </span>
      <span class="col-span-12 md:col-span-6">
        <label class="required-sign">{{ translate('proficiency level', 'ucfirst') }}</label>
        <Dropdown 
          v-model="selectedProficiencyId"
          :options="proficiencyLevels" 
          optionLabel="pt" 
          optionValue="proficiency_id" 
          :loading="fetchingProfLevels"
          placeholder="Selecione o nível de proficiência"
          class="w-full px-3 mt-2.5 rounded-full border border-iron" 
        />
      </span>
      <span class="col-span-12 md:col-span-6">
  <label class="required-sign">{{ translate('experience time', 'ucfirst') }}</label>
  <CustomInputText type="number" name="experience_level" class="w-full px-3 py-2 mt-2.5 rounded-full border border-iron" />
      </span>
      <span class="col-span-12">
      <Button
          v-if="Object.keys(props.skillToUpdate).length > 0"
          @click="showConfirmDialog = true"
          type="button"
          :label="translate('delete', 'ucfirst')"
          class="text-white px-5 py-2 float-left rounded-full bg-red-600"
        />
        <Button
          type="submit"
          :label="
            Object.keys(props.skillToUpdate).length > 0
              ? translate('update', 'ucfirst')
              : translate('save', 'ucfirst')
          "
          class="text-white px-5 py-2 float-right rounded-full bg-sea-serpent"
        />
      </span>
    </form>

    <CustomConfirmDialog
      :show-confirm="showConfirmDialog" @confirmResponse="confirmDialogResponse"
    />
    <LoadingDialog :visible="isLoading" />
  </section>
</template>

<script setup>
import { ref, onMounted, onBeforeMount, watch, nextTick } from "vue";
import CustomInputText from "@/components/Base/CustomInputText.vue";
import Dropdown from "primevue/dropdown";
import Button from "primevue/button";
import { useToast } from "primevue/usetoast";
import { showToast } from "@/composables/toast";
import { translate } from "@/composables/translate";
import requestHandler from "@/composables/requestHandler";
import LoadingDialog from "@/components/Utils/LoadingDialog.vue";
import { useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/yup";
import { object, string, number } from "yup";
import CustomConfirmDialog from "@/components/Utils/CustomConfirmDialog.vue";
import useSkillProficiencyLevels from "@/composables/skillProficiencyLevels";

let toast = useToast();
let isLoading = ref(false);
let showConfirmDialog = ref(false);

// Usar o composable para níveis de proficiência de habilidades
const { 
  proficiencyLevels, 
  isLoading: fetchingProfLevels, 
  fetchSkillProficiencyLevels
} = useSkillProficiencyLevels();

// Variável reativa para o dropdown de proficiência
const selectedProficiencyId = ref(null);

const props = defineProps({
  skillToUpdate: {
    type: Object,
    required: false,
    default: () => {}
  },
  curriculumId: {
    type: Number,
    required: false
  },
});

const emit = defineEmits(["closeModal",'skillsUpdated']);

// ############ Validação formulário ############ 2

const { handleSubmit, setValues } = useForm({
  validationSchema: toTypedSchema(
    object().shape({
      skill_name: string()
        .required(translate("this field is required", "ucfirst"))
        .min(2, translate("this field must be at least 2 characters", "ucfirst"))
        .max(255, translate("this field must be at most 255 characters", "ucfirst"))
        .ensure().trim().default(""),
      experience_level: number(translate('this field must be a number', 'ucfirst')).required(translate('this field is required', 'ucfirst'))
    })
  ),
});

// ############ Criação de Métodos/Funções ############

const addNewSkill = handleSubmit(async (values) => {

  if (values) {
    // Validar se o nível de proficiência foi selecionado
    if (!selectedProficiencyId.value) {
      showToast(toast, {
        severity: 'error',
        summary: translate('error', 'ucfirst'),
        detail: translate('proficiency level is required', 'ucfirst')
      });
      return;
    }
    
    isLoading.value = !isLoading.value;
    
    // Usar o valor selecionado do dropdown
    const skillData = {
      ...values,
      proficiency_level: selectedProficiencyId.value,
      skproficiency_level: selectedProficiencyId.value
    };
    const response = await requestHandler.executeRequest("/api/curriculum/skill/create", skillData ,"post");

    isLoading.value = !isLoading.value;

    if (!response.success) {
      showToast(toast,
        translate("there was an error adding your skill", "ucfirst"),"error");
      return;
    }

    showToast(toast, translate("added skill", "ucfirst"));
    emit("skillsUpdated", true);
    emit("closeModal", false);
  }
});

let updateSkill = handleSubmit(async (values) => {

//   const newValues = {
//   value: values.value,
//   add_information_type_id: values.add_information_type_id,
//   add_information_order: values.add_information_order
//  }

  if (values) {
    // Validar se o nível de proficiência foi selecionado
    if (!selectedProficiencyId.value) {
      showToast(toast, {
        severity: 'error',
        summary: translate('error', 'ucfirst'),
        detail: translate('proficiency level is required', 'ucfirst')
      });
      return;
    }
    
    isLoading.value = !isLoading.value;
    
    // Usar o valor selecionado do dropdown
    const skillData = {
      ...values,
      proficiency_level: selectedProficiencyId.value,
      skproficiency_level: selectedProficiencyId.value
    };
    
    const response = await requestHandler.executeRequest(`/api/curriculum/skill/update/${props.skillToUpdate.id}`, skillData ,"put");

    isLoading.value = !isLoading.value;

    if (!response.success) {
      showToast(
        toast,
        translate("there was an error updating your skill", "ucfirst"),
        "warn"
      );
      return;
    }
    showToast(toast, translate("your skill was updated", "ucfirst"));
    emit("skillsUpdated", true);
    emit("closeModal", false);
  }
});

 let deleteSkill = handleSubmit(async (values) => {

  const deleteBody = {
    item_type: "skill",
    item_id: props.skillToUpdate.id,
    curriculum_id: props.curriculumId
  };

  if (values) {
    isLoading.value = !isLoading.value;
    const response = await requestHandler.executeRequest("/api/curriculum/item/delete", deleteBody,"delete");

    isLoading.value = !isLoading.value;

    if (!response.success) {
      showToast(toast,translate("there was an error deleting your skill", "ucfirst"),"warn");
      return;
    }
    showToast(toast, translate("your skill was deleted", "ucfirst"));
    emit("closeModal", false);
    emit("skillsUpdated", true);
  }
});

function confirmDialogResponse(event) {
  
  showConfirmDialog.value = event.close;
  if (event.accepted) {
    deleteSkill()
  } 
}

async function getListproficiencyLevels() {
  await fetchSkillProficiencyLevels();
}


onBeforeMount(() => {
  getListproficiencyLevels()
});

// Watch para reagir quando os níveis de proficiência carregarem
watch(proficiencyLevels, (newLevels) => {
  if (newLevels.length > 0 && Object.keys(props.skillToUpdate).length > 0) {
    loadSkillData();
  }
}, { immediate: true });

// Watch para reagir quando skillToUpdate mudar
watch(() => props.skillToUpdate, (newSkill) => {
  if (Object.keys(newSkill).length > 0 && proficiencyLevels.value.length > 0) {
    loadSkillData();
  }
}, { immediate: true, deep: true });

function loadSkillData() {
  
  if (proficiencyLevels.value.length > 0 && Object.keys(props.skillToUpdate).length > 0) {
    // Mapear proficiência para ID correto, aceitando qualquer campo possível
    let proficiencyId = null;
    const skill = props.skillToUpdate;
    
    // Tenta todos os campos possíveis
    proficiencyId = skill.proficiency_id || skill.proficiency_level || skill.skproficiency_level || skill.level || skill.proficiency || null;
    
    
    // Se for string, buscar pelo nome do nível
    if (typeof proficiencyId === 'string') {
      const proficiency = proficiencyLevels.value.find(p =>
        p.proficiency_level === proficiencyId || p.pt === proficiencyId || p.en === proficiencyId || p.es === proficiencyId
      );

      proficiencyId = proficiency ? proficiency.proficiency_id : proficiencyId;
    }
    
    selectedProficiencyId.value = proficiencyId;
    
    nextTick(() => {
      setValues({
        skill_name: skill.name,
        proficiency_level: proficiencyId,
        experience_level: skill.experience_level
      });
    });
  }
}

onMounted(() => {
  // Se os dados já estão carregados, processar imediatamente
  if (proficiencyLevels.value.length > 0 && Object.keys(props.skillToUpdate).length > 0) {
    loadSkillData();
  }
})
</script>
